import { Badge, Button, cardClass, fieldClass, selectClass, textareaClass } from '@ledgerloom/ui';
import { useState } from 'react';
import { api, type Outcome } from '../api';
import { useApi } from '../hooks';
import { ErrorBanner, Expandable, outcomeMessage, outcomeTone, useToast } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';

// The standard playbook behind an agent (ADR 0019, ADR 0043): which text the row
// runs (the version it is on, a required or pinned one, or a signed override on
// top), a newer release it may upgrade to, the released versions it may pin to,
// and its learnable sections (ADR 0028: all but the invariant ones that hold its
// rules). An upgrade, a pin, or an override is a change set another admin signs;
// the coach drafts overrides too.
// Patterns: data-display/statistics for the resolution, content-management/modal
// for each change, forms/textarea per overridable section.

export interface Standard {
  playbook_id: string; standard_pin: number | null; updates: 'ask' | 'automatic'; available: number | null;
  resolution: { source: 'override' | 'forced' | 'pin' | 'standard' | 'tenant'; standard_version: number | null; override_version: number | null; sha256: string | null };
  versions: Array<{ version: number; status: string; released_at: string | null; withdrawn_at: string | null; tenant_may_override: string[] }>;
  override: { version: number; base_version: number | null; sections: string[]; signed_by: string; signed_at: string } | null;
  overridable: Array<{ heading: string; standard_text: string; override_text: string | null }>;
  invariant: string[];
}

const SOURCE: Record<Standard['resolution']['source'], (s: Standard) => string> = {
  tenant: () => 'The copy signed at hire.',
  standard: (s) => `Standard version ${s.resolution.standard_version}.`,
  forced: (s) => `Standard version ${s.resolution.standard_version}, which Mazu required.`,
  pin: (s) => `Pinned to standard version ${s.resolution.standard_version}. New releases do not change it.`,
  override: (s) => `${s.resolution.standard_version === null ? 'The copy signed at hire' : `Standard version ${s.resolution.standard_version}`} with this workspace's override (version ${s.resolution.override_version}) on ${s.override?.sections.map(plain).join(', ')}.`,
};

const plain = (h: string) => h.replace(/^## /, '');

export function AgentStandard({ agentId, canChange, onChanged }: { agentId: string; canChange: boolean; onChanged: () => void }) {
  const std = useApi<Standard>(`/v1/agents/${encodeURIComponent(agentId)}/standard`, [agentId]);
  const [action, setAction] = useState<'pin' | 'override' | 'upgrade' | null>(null);
  const [pin, setPin] = useState<string>('');
  const [sections, setSections] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const s = std.data;
  if (!s) return null;
  if (!s.versions.length && !s.overridable.length) return null;
  const released = s.versions.filter((v) => v.status === 'released');

  const open = (x: 'pin' | 'override' | 'upgrade') => {
    setError(null); setReason('');
    if (x === 'pin') setPin(s.standard_pin === null ? '' : String(s.standard_pin));
    if (x === 'override') setSections(Object.fromEntries(s.overridable.map((o) => [o.heading, o.override_text ?? ''])));
    setAction(x);
  };
  const finish = (out: Outcome, saved: string) => { toast(outcomeMessage(out, saved), outcomeTone(out)); setAction(null); void std.reload(); onChanged(); };
  async function savePin() {
    setBusy(true); setError(null);
    try {
      const out = await api<Outcome>(`/v1/agents/${encodeURIComponent(agentId)}/pin`, { body: { standard_version: pin === '' ? null : Number(pin), ...(reason ? { reason } : {}) } });
      finish(out, pin === '' ? 'Pin lifted' : `Pinned to version ${pin}`);
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  async function saveUpgrade() {
    setBusy(true); setError(null);
    try {
      const out = await api<Outcome>(`/v1/agents/${encodeURIComponent(agentId)}/upgrade`, { body: { standard_version: s!.available, ...(reason ? { reason } : {}) } });
      finish(out, `Upgraded to version ${s!.available}`);
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  async function saveOverride() {
    setBusy(true); setError(null);
    try {
      // Every learnable section goes in the delta: its text, or null for an empty box, which means the playbook's own text.
      const delta = Object.fromEntries(Object.entries(sections).map(([h, t]) => [h, t.trim() || null]));
      const out = await api<Outcome>(`/v1/agents/${encodeURIComponent(agentId)}/override`, { body: { sections: delta, ...(reason ? { reason } : {}) } });
      finish(out, 'Override proposed');
    } catch (e) { setError(e); } finally { setBusy(false); }
  }

  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 8 }} aria-labelledby="standard-h">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'start', gap: 8, flexWrap: 'wrap' }}>
        <div><h2 id="standard-h" style={{ margin: 0 }}>Standard playbook</h2><div className="small faint">{s.playbook_id}</div></div>
        {canChange && (
          <div className="row" style={{ gap: 8 }}>
            {s.available !== null && <Button type="submit" size="sm" onClick={() => open('upgrade')}>Upgrade to version {s.available}</Button>}
            {released.length > 0 && <Button type="submit" variant="secondary" size="sm" onClick={() => open('pin')}>{s.standard_pin === null ? 'Pin a version' : 'Change pin'}</Button>}
            {s.overridable.length > 0 && <Button type="submit" variant="secondary" size="sm" onClick={() => open('override')}>{s.override ? 'Change override' : 'Override a section'}</Button>}
          </div>
        )}
      </div>
      <p className="small" style={{ margin: 0 }} data-testid="standard-resolution">{SOURCE[s.resolution.source](s)}</p>
      <p className="small faint" style={{ margin: 0 }} data-testid="standard-updates">{s.updates === 'automatic' ? 'This workspace updates agents automatically.' : 'New versions wait for someone here to sign the upgrade.'}</p>
      {s.overridable.length > 0 && (
        <Expandable label={[`Show the ${s.overridable.length} section${s.overridable.length === 1 ? '' : 's'} this workspace may override`, 'Hide the sections']}>
          <dl className="small" style={{ margin: 0 }}>
            {s.overridable.map((o) => (
              <div key={o.heading} style={{ marginBottom: 8 }}>
                <dt><strong>{o.heading.replace(/^## /, '')}</strong>{o.override_text !== null && <Badge tone="accent" style={{ marginLeft: 6 }}>overridden</Badge>}</dt>
                <dd style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap' }}>{(o.override_text ?? o.standard_text) || <span className="faint">(empty)</span>}</dd>
              </div>
            ))}
          </dl>
        </Expandable>
      )}
      <ConfirmDialog open={action === 'upgrade'} title={`Upgrade to version ${s.available}`} confirmLabel="Propose this upgrade" busy={busy} onConfirm={saveUpgrade} onCancel={() => setAction(null)}>
        <p style={{ margin: 0 }}>The agent takes this version's text and settings: what it may propose and reach, when it runs, and when it stops. Limits you set stay, and so do triggers or stop conditions you changed. Your override stays where the new version still allows it. Another admin signs the change.</p>
        <ErrorBanner error={error} />
        <Field label="Why?" hint="Optional. Saved with the change set."><input className={fieldClass} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </ConfirmDialog>
      <ConfirmDialog open={action === 'pin'} title="Pin the standard version" confirmLabel="Propose this pin" busy={busy} onConfirm={savePin} onCancel={() => setAction(null)}>
        <p style={{ margin: 0 }}>A pinned agent stays on one released version, even if this workspace updates automatically. Only a version Mazu requires moves it. Another admin signs the change.</p>
        <ErrorBanner error={error} />
        <Field label="Version">
          <select className={selectClass} value={pin} onChange={(e) => setPin(e.target.value)}>
            <option value="">No pin</option>
            {s.versions.map((v) => <option key={v.version} value={String(v.version)}>Version {v.version}{v.status === 'withdrawn' ? ' (withdrawn)' : ''}</option>)}
          </select>
        </Field>
        <Field label="Why?" hint="Optional. Saved with the change set."><input className={fieldClass} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </ConfirmDialog>
      <ConfirmDialog open={action === 'override'} title="Override sections of the standard" confirmLabel="Propose this override" busy={busy} onConfirm={saveOverride} onCancel={() => setAction(null)}>
        <p style={{ margin: 0 }}>The sections that hold this agent's rules{s.invariant.length ? ` (${s.invariant.map(plain).join(', ')})` : ''} never change. The override is a delta on the playbook, so a new release keeps your sections and brings everything else. Leave a box empty to use the playbook's own text. Another admin signs the change.</p>
        <ErrorBanner error={error} />
        {s.overridable.map((o) => (
          <Field key={o.heading} label={o.heading.replace(/^## /, '')} hint={o.standard_text ? `Standard: ${o.standard_text.slice(0, 160)}${o.standard_text.length > 160 ? '…' : ''}` : undefined}>
            <textarea className={textareaClass} rows={3} value={sections[o.heading] ?? ''} onChange={(e) => setSections({ ...sections, [o.heading]: e.target.value })} />
          </Field>
        ))}
        <Field label="Why?" hint="Optional. Saved with the change set."><input className={fieldClass} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </ConfirmDialog>
    </section>
  );
}
